import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Navigation from "./Navigation";
import { PINNED_PANEL_TOP } from "./PinnedNavigation";
import Card from "./Card";
import LoadingSpinner from "./LoadingSpinner";
import { BookOpen } from "lucide-react";
import {
  useFeatureFlagsLoaded,
  useRemediationWorkflows,
  useShowAiIntelligence,
  useUsoc,
  useFusion,
  useRemediationScorecard,
} from "../contexts/FeatureFlagsContext";
import { FEATURE_FLAGS, guideFlagFor, stripFeatureBlocks } from "./userGuideGating";

// The guide content is generated from docs/user-guide by tools/sync-user-guide.mjs
// into public/user-guide (markdown + images + index.json). Static assets on our own
// origin, so plain fetch is correct here — useApi is only for API calls.
// Guides and sections behind a feature flag are filtered at render time (userGuideGating.js);
// index.json and the mirror always contain the full manual.
const BASE = `${process.env.PUBLIC_URL || ""}/user-guide`;

// Exported for tests: the `a` renderer is what keeps links to gated guides from going anywhere.
export const markdownComponents = (navigate, isGuideHidden) => ({
  h1: (props) => <h1 className="text-2xl font-bold text-content-primary mb-4" {...props} />,
  h2: (props) => <h2 className="text-lg font-semibold text-content-primary mt-8 mb-3 pb-1 border-b border-border" {...props} />,
  h3: (props) => <h3 className="text-base font-semibold text-content-primary mt-6 mb-2" {...props} />,
  p: (props) => <p className="text-sm text-content-secondary leading-relaxed mb-3" {...props} />,
  ul: (props) => <ul className="list-disc pl-6 mb-3 space-y-1 text-sm text-content-secondary" {...props} />,
  ol: (props) => <ol className="list-decimal pl-6 mb-3 space-y-1 text-sm text-content-secondary" {...props} />,
  li: (props) => <li className="leading-relaxed" {...props} />,
  strong: (props) => <strong className="font-semibold text-content-primary" {...props} />,
  blockquote: (props) => <blockquote className="border-l-4 border-accent-cyan/40 bg-theme-tertiary rounded-r-md px-4 py-2 mb-3 text-sm text-content-secondary" {...props} />,
  code: (props) => <code className="px-1 py-0.5 rounded bg-theme-tertiary text-accent-cyan text-xs" {...props} />,
  hr: () => <hr className="my-6 border-border" />,
  table: (props) => (
    <div className="overflow-x-auto mb-4">
      <table className="min-w-full text-sm border border-border rounded-md" {...props} />
    </div>
  ),
  th: (props) => <th className="text-left px-3 py-2 bg-theme-tertiary text-content-primary font-semibold border-b border-border" {...props} />,
  td: (props) => <td className="px-3 py-2 text-content-secondary border-b border-border align-top" {...props} />,
  img: ({ src, alt, title }) => {
    // Optional `w=NNN` in the markdown image title caps the display width in px
    // (set per image via the manifest's `width` field). Stays responsive: never
    // wider than the column, and shrinks on narrow screens.
    const w = /(?:^|[\s;])w=(\d+)/.exec(title || "");
    return (
      <img
        src={src?.startsWith("images/") ? `${BASE}/${src}` : src}
        alt={alt}
        title={w ? undefined : title}
        loading="lazy"
        style={w ? { maxWidth: `${w[1]}px` } : undefined}
        className="rounded-lg border border-border shadow-sm my-4 max-w-full h-auto"
      />
    );
  },
  a: ({ href, children }) => {
    // Cross-guide links are `<slug>.md`, optionally with an anchor. The anchor is dropped: the
    // viewer always opens the guide at the top.
    const md = /^(?:\.\/)?([^/#?]+)\.md(?:#.*)?$/.exec(href || "");
    if (md) {
      const slug = md[1];
      // A link to a guide hidden by its feature flag renders as plain text: following it would
      // only bounce the reader back to the index. Belt and braces — the linking text itself
      // should sit inside a `<!-- feature: ... -->` block.
      if (isGuideHidden(slug)) return <>{children}</>;
      return (
        <button
          onClick={() => navigate(`/user-guide/${slug}`)}
          className="text-left text-accent-cyan hover:underline"
        >
          {children}
        </button>
      );
    }
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent-cyan hover:underline">
        {children}
      </a>
    );
  },
});

const UserGuide = () => {
  const { docId } = useParams();
  const navigate = useNavigate();
  const [index, setIndex] = useState([]);
  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(true);
  const current = docId || "README";

  // Each gated flag goes through its own hook so the hook's default semantics hold: opt-in flags
  // (remediationWorkflows) read false until the backend returns true; default-visible flags
  // (showAiIntelligence) read true until the backend explicitly returns false.
  const flagsLoaded = useFeatureFlagsLoaded();
  const remediationWorkflows = useRemediationWorkflows();
  const showAiIntelligence = useShowAiIntelligence();
  const usoc = useUsoc();
  const fusion = useFusion();
  const remediationScorecard = useRemediationScorecard();
  const flags = useMemo(
    () => ({
      [FEATURE_FLAGS.RemediationWorkflows]: remediationWorkflows,
      [FEATURE_FLAGS.ShowAiIntelligence]: showAiIntelligence,
      [FEATURE_FLAGS.Usoc]: usoc,
      [FEATURE_FLAGS.Fusion]: fusion,
      [FEATURE_FLAGS.RemediationScorecard]: remediationScorecard,
    }),
    [remediationWorkflows, showAiIntelligence, usoc, fusion, remediationScorecard]
  );
  // Until the flags have loaded every gated guide counts as off, whatever its hook's default:
  // a default-visible flag would otherwise list and render the guide, then redirect after the
  // backend's false arrives.
  const visibleIndex = useMemo(
    () => index.filter((item) => {
      const flagName = guideFlagFor(item.slug);
      return !flagName || (flagsLoaded && flags[flagName]);
    }),
    [index, flags, flagsLoaded]
  );
  const isGuideHidden = useCallback(
    (slug) => {
      const flagName = guideFlagFor(slug);
      return Boolean(flagName) && (!flagsLoaded || !flags[flagName]);
    },
    [flags, flagsLoaded]
  );
  const gatedOff = isGuideHidden(current);
  const components = useMemo(() => markdownComponents(navigate, isGuideHidden), [navigate, isGuideHidden]);

  useEffect(() => {
    fetch(`${BASE}/index.json`)
      .then((r) => r.json())
      .then(setIndex)
      .catch(() => setIndex([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    if (gatedOff) {
      // Direct URL to a gated guide: wait for the flags (an opt-in flag reads false until they
      // arrive), then send the user to the guide index instead of the content.
      if (flagsLoaded) navigate("/user-guide", { replace: true });
      return;
    }
    let cancelled = false;
    fetch(`${BASE}/${encodeURIComponent(current)}.md`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.text();
      })
      .then((text) => {
        if (!cancelled) setContent(stripFeatureBlocks(text, flags));
      })
      .catch(() => {
        if (!cancelled) setContent("# Guide not found\n\nUse the table of contents to pick a guide.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    window.scrollTo(0, 0);
    return () => {
      cancelled = true;
    };
  }, [current, gatedOff, flagsLoaded, flags, navigate]);

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <BookOpen className="h-8 w-8 text-accent-cyan" />
          <h1 className="text-2xl font-bold text-content-primary">User Guide</h1>
        </div>
        <div className="flex gap-6 items-start">
          <aside className={`hidden md:block w-64 flex-shrink-0 sticky ${PINNED_PANEL_TOP}`}>
            <Card variant="elevated">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-content-muted mb-2">Contents</h2>
              <nav className="space-y-0.5">
                {visibleIndex.map((item) => (
                  <button
                    key={item.slug}
                    onClick={() => navigate(item.slug === "README" ? "/user-guide" : `/user-guide/${item.slug}`)}
                    className={`block w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${
                      current === item.slug
                        ? "bg-accent-cyan/10 text-accent-cyan font-medium"
                        : "text-content-secondary hover:text-content-primary hover:bg-theme-hover"
                    }`}
                  >
                    {item.title}
                  </button>
                ))}
              </nav>
            </Card>
          </aside>
          <main className="flex-1 min-w-0">
            <Card variant="elevated">
              {/* Mobile TOC */}
              <div className="md:hidden mb-4">
                <select
                  value={current}
                  onChange={(e) => navigate(e.target.value === "README" ? "/user-guide" : `/user-guide/${e.target.value}`)}
                  className="w-full px-3 py-2 rounded-lg bg-theme-tertiary border border-border text-content-primary text-sm"
                >
                  {visibleIndex.map((item) => (
                    <option key={item.slug} value={item.slug}>{item.title}</option>
                  ))}
                </select>
              </div>
              {loading ? (
                <div className="py-16 flex justify-center"><LoadingSpinner /></div>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
                  {content}
                </ReactMarkdown>
              )}
            </Card>
          </main>
        </div>
      </div>
    </div>
  );
};

export default UserGuide;
